import { lazy, Suspense, useEffect, useMemo } from "react";
import { ConfigProvider } from "./hooks/ConfigProvider";
import { useConfiguration } from "./hooks/useConfiguration";
import { useGliderDb } from "./hooks/useGliderDb";
import { useUrlSync } from "./hooks/useUrlSync";
import { ASW27 } from "./lib/glider";
import { findGlider } from "./lib/glider-db";
import { effectiveCoeffs } from "./lib/polar";
import { loadingFactor } from "./lib/mass";
import { computeOutputs } from "./lib/outputs";
import { buildChartSummary } from "./lib/summary";
import { ConfigSliders } from "./components/ConfigSliders";
import { GliderInfo } from "./components/GliderInfo";
import { GliderPicker } from "./components/GliderPicker";
import { ShareButton } from "./components/ShareButton";
import { StatCards } from "./components/StatCards";
import { UnitToggle } from "./components/UnitToggle";

// Chart.js is the heaviest dependency and not needed for first paint, so the
// chart is code-split into its own chunk and streamed in after the shell.
const PolarChart = lazy(() =>
  import("./components/PolarChart").then((m) => ({ default: m.PolarChart })),
);

function PolarApp() {
  const { config, dispatch } = useConfiguration();
  const { gliders, ensureLoaded } = useGliderDb();
  useUrlSync(config, dispatch);

  // ASW-27 is bootstrapped from a constant so the default renders without
  // pulling the full database; once loaded, the selected id resolves from it.
  const glider =
    (gliders !== null ? findGlider(gliders, config.gliderId) : undefined) ?? ASW27;

  // If a shared link names a non-default glider, eagerly load the database so
  // its real polar replaces the ASW-27 bootstrap.
  useEffect(() => {
    if (config.gliderId !== ASW27.id) ensureLoaded();
  }, [config.gliderId, ensureLoaded]);

  const {
    pilotWeight_kg,
    waterBallast_kg,
    bugsPercent,
    macCready_ms,
    windHeadwind_kmh,
    units,
  } = config;

  // Derive the wing-loading factor k from the physical mass model; a shared
  // link may overflow this glider's tanks, so clamp water to its placarded max.
  const wingLoadingFactor = useMemo(
    () =>
      loadingFactor(
        glider,
        pilotWeight_kg,
        Math.min(waterBallast_kg, glider.maxBallast_kg),
      ),
    [glider, pilotWeight_kg, waterBallast_kg],
  );

  const coeffs = useMemo(
    () => effectiveCoeffs(glider.polar, wingLoadingFactor, bugsPercent),
    [glider.polar, wingLoadingFactor, bugsPercent],
  );
  const outputs = useMemo(
    () => computeOutputs(coeffs, macCready_ms, windHeadwind_kmh),
    [coeffs, macCready_ms, windHeadwind_kmh],
  );
  const showReference = wingLoadingFactor !== 1 || bugsPercent !== 0;
  const ariaLabel = buildChartSummary(glider.model, outputs, units, wingLoadingFactor);

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-panel px-5 py-3.5">
        <h1 className="text-lg font-semibold">
          Polar
          <span className="ml-2 text-sm font-normal text-muted">
            glider polar curve explorer
          </span>
        </h1>
        <div className="flex items-center gap-3">
          <ShareButton />
          <UnitToggle />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:grid md:grid-cols-[320px_1fr] md:overflow-hidden">
        <aside className="border-b border-border bg-panel p-[18px] md:overflow-y-auto md:border-b-0 md:border-r">
          <GliderPicker current={glider} gliders={gliders} onOpen={ensureLoaded} />
          <GliderInfo glider={glider} />
          <ConfigSliders glider={glider} />
        </aside>

        <main className="flex min-h-0 flex-1 flex-col p-[18px]">
          <Suspense
            fallback={
              <div className="min-h-[340px] flex-1 rounded-[10px] border border-border bg-panel" />
            }
          >
            <PolarChart
              coeffs={coeffs}
              baseline={glider.polar}
              outputs={outputs}
              units={units}
              windHeadwind_kmh={windHeadwind_kmh}
              macCready_ms={macCready_ms}
              showReference={showReference}
              ariaLabel={ariaLabel}
            />
          </Suspense>
          <StatCards
            outputs={outputs}
            units={units}
            macCready_ms={macCready_ms}
            windHeadwind_kmh={windHeadwind_kmh}
          />
          <footer className="pt-2.5 text-center text-[11px] text-muted">
            Polar data: XCSoar project (GPL-2.0)
          </footer>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ConfigProvider>
      <PolarApp />
    </ConfigProvider>
  );
}
